/** Pantalla de inicio: galería de pizarras (crear, abrir, renombrar, eliminar). */
import type { BoardSummary } from '../../shared/types.ts'
import { api } from '../api.ts'
import { closePopover, h, openDialog, openMenuAtPoint, toast } from './dom.ts'
import { icon } from './icons.ts'
import { openConfirmDialog } from './dialogs.ts'

export class Gallery {
  readonly el: HTMLElement
  private grid: HTMLElement
  private userName: string
  private onOpenBoard: (id: string) => void
  private onChangeName: () => void

  constructor(options: { userName: string; onOpenBoard: (id: string) => void; onChangeName: () => void }) {
    this.userName = options.userName
    this.onOpenBoard = options.onOpenBoard
    this.onChangeName = options.onChangeName
    this.grid = h('div', { class: 'board-grid' })
    this.el = h('div', { class: 'gallery' }, [
      h('div', { class: 'gallery-inner' }, [
        h('div', { class: 'gallery-head' }, [
          h('div', { class: 'brand-mark' }, [
            h('div', { class: 'logo', html: icon('pen', 22) }),
            h('div', {}, [
              h('h1', {}, ['Pizarra La Unitec']),
              h('span', {}, ['Lienzo infinito colaborativo en tiempo real']),
            ]),
          ]),
          h('div', { style: 'display:flex;gap:8px;align-items:center' }, [
            h('button', { class: 'btn-secondary', onclick: () => this.onChangeName() }, [
              h('span', { html: icon('users', 18), style: 'display:flex' }),
              this.userName,
            ]),
            h('button', { class: 'btn-primary', onclick: () => void this.createBoard() }, [
              h('span', { html: icon('plus', 18), style: 'display:flex' }),
              'Nueva pizarra',
            ]),
          ]),
        ]),
        this.grid,
      ]),
    ])
  }

  async refresh(): Promise<void> {
    try {
      const boards = await api.listBoards()
      this.render(boards)
    } catch {
      toast('No se pudo cargar la lista de pizarras')
    }
  }

  private render(boards: BoardSummary[]): void {
    const cards: HTMLElement[] = [
      h('button', { class: 'new-board', onclick: () => void this.createBoard() }, [
        h('span', { html: icon('plus', 28) }),
        h('span', {}, ['Nueva pizarra']),
      ]),
    ]

    for (const board of boards) {
      const thumb = h('div', { class: 'thumb' })
      if (board.thumb) thumb.style.backgroundImage = `url(${board.thumb})`
      else thumb.innerHTML = icon(board.kind === 'mindmap' ? 'mindmap' : 'pen', 28)
      if (board.kind === 'mindmap') {
        thumb.append(h('span', { class: 'kind-badge' }, ['Mapa']))
      }

      const card = h(
        'button',
        {
          class: 'board-card',
          onclick: () => this.onOpenBoard(board.id),
        },
        [
          thumb,
          h('div', { class: 'meta' }, [
            h('strong', {}, [board.name]),
            h('small', {}, [`${formatDate(board.updatedAt)} · ${board.ownerName || 'La Unitec'}`]),
          ]),
        ],
      )
      const menu = h('span', {
        class: 'card-menu',
        html: icon('menu', 16),
        onclick: (e: Event) => {
          e.stopPropagation()
          const ev = e as MouseEvent
          openMenuAtPoint(
            h('div', { class: 'menu-list' }, [
              h(
                'button',
                {
                  class: 'menu-item',
                  onclick: () => {
                    closePopover()
                    this.rename(board)
                  },
                },
                ['Cambiar nombre'],
              ),
              h(
                'button',
                {
                  class: 'menu-item',
                  onclick: () => {
                    closePopover()
                    void this.duplicate(board)
                  },
                },
                ['Duplicar'],
              ),
              h(
                'button',
                {
                  class: 'menu-item is-danger',
                  onclick: () => {
                    closePopover()
                    openConfirmDialog({
                      title: 'Eliminar pizarra',
                      message: `Se eliminará "${board.name}" para todos. Esta acción no se puede deshacer.`,
                      confirmLabel: 'Eliminar',
                      onConfirm: () => void this.remove(board.id),
                    })
                  },
                },
                ['Eliminar'],
              ),
            ]),
            ev.clientX,
            ev.clientY,
          )
        },
      })
      card.append(menu)
      cards.push(card)
    }

    this.grid.replaceChildren(...cards)
  }

  /** Pregunta qué crear: pizarra libre o mapa mental (estilo XMind). */
  private createBoard(): void {
    openDialog((close) => {
      const opcion = (
        icono: string,
        titulo: string,
        detalle: string,
        kind: 'board' | 'mindmap',
      ): HTMLElement =>
        h(
          'button',
          {
            class: 'board-kind-option',
            onclick: () => {
              close()
              void this.reallyCreate(kind)
            },
          },
          [
            h('span', { class: 'board-kind-icon', html: icon(icono, 30) }),
            h('span', { class: 'board-kind-title' }, [titulo]),
            h('span', { class: 'board-kind-detail' }, [detalle]),
          ],
        )
      return h('div', {}, [
        h('h2', { class: 'dialog-title' }, ['¿Qué quieres crear?']),
        h('div', { class: 'board-kind-row' }, [
          opcion('pen', 'Pizarra', 'Lienzo libre: tinta, notas, figuras e imágenes', 'board'),
          opcion('mindmap', 'Mapa mental', 'Temas conectados que se organizan solos, horizontal o vertical', 'mindmap'),
        ]),
      ])
    })
  }

  private async reallyCreate(kind: 'board' | 'mindmap'): Promise<void> {
    try {
      const board = await api.createBoard(kind === 'mindmap' ? 'Mapa mental sin título' : 'Pizarra sin título', kind)
      this.onOpenBoard(board.id)
    } catch {
      toast('No se pudo crear la pizarra')
    }
  }

  private rename(board: BoardSummary): void {
    openDialog((close) => {
      const input = h('input', { class: 'input', value: board.name }) as HTMLInputElement
      const save = async (): Promise<void> => {
        const name = input.value.trim() || board.name
        close()
        try {
          await api.renameBoard(board.id, name)
        } catch {
          toast('No se pudo cambiar el nombre')
        }
        await this.refresh()
      }
      input.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') void save()
        e.stopPropagation()
      })
      return h('div', {}, [
        h('h2', {}, ['Cambiar nombre']),
        h('div', { class: 'field' }, [input]),
        h('div', { class: 'dialog-actions' }, [
          h('button', { class: 'btn-secondary', onclick: () => close() }, ['Cancelar']),
          h('button', { class: 'btn-primary', onclick: () => void save() }, ['Guardar']),
        ]),
      ])
    })
  }

  private async duplicate(board: BoardSummary): Promise<void> {
    try {
      await api.duplicateBoard(board.id)
      await this.refresh()
      toast('Pizarra duplicada')
    } catch {
      toast('No se pudo duplicar')
    }
  }

  private async remove(id: string): Promise<void> {
    try {
      await api.deleteBoard(id)
      await this.refresh()
    } catch {
      toast('No se pudo eliminar')
    }
  }

  setUserName(name: string): void {
    this.userName = name
  }
}

function formatDate(ts: number): string {
  const date = new Date(ts)
  const now = Date.now()
  const diff = now - ts
  if (diff < 60_000) return 'hace un momento'
  if (diff < 3_600_000) return `hace ${Math.floor(diff / 60_000)} min`
  if (diff < 86_400_000) return `hace ${Math.floor(diff / 3_600_000)} h`
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
}
